/**
 * The single "Coming up" row for customer invoices past their due date. An
 * invoice someone chased in the last few days is counted as handled, so Home
 * does not nag about it again; the Sales register still lists it with its
 * "Chased" line.
 */

import type { Invoice } from '../../types/accounting';
import { formatInvoiceMoney, isInvoiceOverdue, londonToday } from '../../shared-ui/invoices/invoiceRowModel';
import { RECENTLY_CHASED_DAYS } from '../interim/chaseLog';
import { londonDaysBetween } from '../interim/variationPending';
import type { ActionItem } from './actionQueueItems';

export interface InvoiceChaseStamp { invoiceId: string; chasedAt: string }
export interface OverdueInvoiceRead { invoices: Invoice[]; chases: InvoiceChaseStamp[] }

/** Overdue unpaid invoices are the only ones worth reading; the rest never leave the Home read. */
export const OVERDUE_INVOICE_STATUSES = ['sent', 'approved', 'overdue'] as const;

const parseDay = (iso: string): Date => new Date(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));

export function overdueInvoiceRow(
  invoices: readonly Invoice[],
  chases: readonly InvoiceChaseStamp[],
  now: Date = new Date(),
): ActionItem | null {
  const today = londonToday(now);
  const chasedAt = new Map(chases.map((c) => [c.invoiceId, new Date(c.chasedAt)]));
  const open = invoices.filter((inv) => inv.invoiceType === 'outbound' && inv.dueDate && isInvoiceOverdue(inv, today));
  const toChase = open.filter((inv) => {
    const last = inv.id ? chasedAt.get(inv.id) : undefined;
    return !last || londonDaysBetween(last, now) >= RECENTLY_CHASED_DAYS;
  });
  if (toChase.length === 0) return null;

  const oldestDue = toChase.map((inv) => (inv.dueDate as string).slice(0, 10)).sort()[0];
  const daysLate = londonDaysBetween(parseDay(oldestDue), parseDay(today));
  const currencies = new Set(toChase.map((inv) => inv.currency));
  // Adding pounds to euros would state a figure nobody owes.
  const owed = currencies.size === 1
    ? `${formatInvoiceMoney(toChase.reduce((sum, inv) => sum + (inv.amountDue ?? inv.total), 0), [...currencies][0])} owed, `
    : '';
  const handled = open.length - toChase.length;
  const count = toChase.length;
  return {
    id: 'sales-invoices-overdue',
    title: `${count} customer invoice${count === 1 ? '' : 's'} overdue`,
    context: `${owed}oldest ${daysLate} day${daysLate === 1 ? '' : 's'} late${handled > 0 ? `, ${handled} chased recently` : ''}`,
    due: parseDay(oldestDue),
    overdue: true,
    target: { view: 'sales', invoiceFilter: 'overdue' },
  };
}
